import {Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {FxRateRateType, FxRateTypeDisplayText} from '../../constants/fx-rate-constants';
import {FxRateReportModel} from '../../models/fx-rate-report.model';

@Component({
    standalone: false,
    selector: 'particle-report-settings-form',
  templateUrl: './report-settings-form.component.html',
  styleUrls: ['./report-settings-form.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportSettingsFormComponent implements OnInit {
  title = 'Report Settings';
  form: FormGroup;
  inputForm: any;
  selectedRateType: any;
  inputFormError: any;
  types = FxRateRateType;
  spotDates: FormArray;
  today: Date;
  summaryRateViews = ['Simple Average', 'Weighted Average', '12 Month Average', '13 Month Average'];
  detailsRateViews = ['Daily', 'Month-End'];
  fxRateFormatOptions = [
    {title: 'Multiply By', key: 'MultiplyBy', isChecked: false},
    {title: 'Divide By', key: 'DivideBy', isChecked: false}
  ];
  @Input() reportModel: FxRateReportModel;
  @Input() disableRateFormatSelection: boolean;
  @Output() formValueChanged: EventEmitter<any> = new EventEmitter();
  @Output() validationChange: EventEmitter<boolean> = new EventEmitter(true);
  rateTypes: any[] = [
    {
      displayText: 'Tax Year Rate',
      value: FxRateRateType.TaxYearRate
    },
    {
      displayText: 'Period Rate',
      value: FxRateRateType.PeriodRate
    },
    {
      displayText: 'Spot Rate',
      value: FxRateRateType.SpotRate
    }
  ];

  constructor(private formBuilder: FormBuilder) {
    this.inputFormError = {
      reportTitle: [],
      rateType: {},
      taxYearEnd: {},
      startDate: {},
      endDate: {},
      spotDates: {},
      summaryRateView: {},
      detailsRateView: {},
      fxRateFormats: []
    };
    this.inputForm = this.formBuilder.group({
      reportTitle: [{value: '', disabled: true}, Validators.required],
      rateType: [{value: '', disabled: true}, Validators.required],
      taxYearEnd: ['', Validators.required],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      spotDates: this.formBuilder.array([this.createDate()]),
      summaryRateView: [{value: this.summaryRateViews[0]}, Validators.required],
      detailsRateView: [{value: this.detailsRateViews[0]}, Validators.required],
      fxRateFormats: [{value: this.fxRateFormatOptions[0]}, Validators.required]
    });

    this.inputForm.valueChanges.subscribe(() => {
      this.onFormValuesChanged();
    });
  }

  onFormValuesChanged() {
    this.formValueChanged.emit(this.inputForm);
    for (const field in this.inputForm) {
      if (!this.inputForm.hasOwnProperty(field)) {
        continue;
      }
      this.inputFormError[field] = {};
      const control = this.inputForm.get(field);
      if (control && control.dirty && !control.valid) {
        this.inputFormError[field] = control.errors;
      }
    }
    this.validationChange.emit(true);
  }

  createDate(): FormGroup {
    return this.formBuilder.group({
      date: ['', Validators.required]
    });
  }

  addDate() {
    this.spotDates = this.inputForm.get('spotDates') as FormArray;
    this.spotDates.push(this.createDate());
  }

  deleteDate(index: number) {
    this.spotDates = this.inputForm.get('spotDates') as FormArray;
    if (index >= 0) {
      this.spotDates.removeAt(index);
    }
  }

  ngOnInit() {
    this.today = new Date();

    if (this.reportModel) {
      this.formValueChanged.emit(this.inputForm);
      this.inputForm.controls['reportTitle'].setValue(this.reportModel.reportName);
      if (!this.reportModel.fxRateFormats) {
        this.reportModel.fxRateFormats = ['MultiplyBy'];
      }
      this.reportModel.fxRateFormats.forEach(fxRateFormat => {
        this.fxRateFormatOptions.forEach(fxRateFormatOpt => {
          if (fxRateFormat === fxRateFormatOpt.key) {
            fxRateFormatOpt.isChecked = true;
          }
        });
      });
      this.inputForm.controls['fxRateFormats'].setValue(this.reportModel.fxRateFormats);
      if (this.reportModel.rateType === FxRateRateType.TaxYearRate) {
        this.inputForm.controls['rateType'].setValue(FxRateTypeDisplayText.TaxYearRate);
        if (this.reportModel.dates && this.reportModel.dates[0]) {
          this.inputForm.controls['taxYearEnd'].setValue(this.reportModel.dates[0]);
        }
      }
      if (this.reportModel.rateType === FxRateRateType.PeriodRate) {
        this.inputForm.controls['rateType'].setValue(FxRateTypeDisplayText.PeriodRate);
        if (this.reportModel.dates && this.reportModel.dates[0] && this.reportModel.dates[1]) {
          this.inputForm.controls['startDate'].setValue(this.reportModel.dates[0]);
          this.inputForm.controls['endDate'].setValue(this.reportModel.dates[1]);
        }
      }
      if (this.reportModel.rateType === FxRateRateType.SpotRate) {
        this.inputForm.controls['rateType'].setValue(FxRateTypeDisplayText.SpotRate);
        let index: number = 0;
        if (this.reportModel.dates) {
          this.reportModel.dates.forEach(d => {
            this.inputForm.controls['spotDates'].controls[index].controls['date'].setValue(d);
            this.addDate();
            index++;
          });
        } else {
          this.addDate();
        }
        this.deleteDate(index);
      }
      // tslint:disable-next-line:triple-equals
      if (this.reportModel.rateType !== FxRateRateType.SpotRate) {
        if (this.reportModel.summaryRateView) {
          this.inputForm.controls['summaryRateView'].setValue(this.reportModel.summaryRateView);
        } else {
          this.inputForm.controls['summaryRateView'].setValue(this.summaryRateViews[0]);
        }
        if (this.reportModel.detailRateView) {
          this.inputForm.controls['detailsRateView'].setValue(this.reportModel.detailRateView);
        } else {
          this.inputForm.controls['detailsRateView'].setValue(this.detailsRateViews[0]);
        }
      } else {
        this.inputForm.controls['summaryRateView'].setValue('Simple Average');
        this.inputForm.controls['detailsRateView'].setValue('Daily');
      }
      this.validationChange.emit(true);
    } else {
      this.inputForm.controls['summaryRateView'].setValue(this.summaryRateViews[0]);
      this.inputForm.controls['detailsRateView'].setValue(this.detailsRateViews[0]);
    }
  }


  isInvalidForm() {
    let isInvalid = false;
    switch (this.reportModel.rateType) {
      case FxRateRateType.TaxYearRate:
        this.inputForm.get('taxYearEnd').markAsTouched();
        isInvalid = isInvalid || this.inputForm.get('taxYearEnd').invalid;
        break;
      case FxRateRateType.PeriodRate:
        this.inputForm.get('startDate').markAsTouched();
        isInvalid = isInvalid || this.inputForm.get('startDate').invalid;
        this.inputForm.get('endDate').markAsTouched();
        isInvalid = isInvalid || this.inputForm.get('endDate').invalid;
        break;
      case FxRateRateType.SpotRate:
        this.inputForm.controls['spotDates'].controls.forEach(o => {
          o.get('date').markAsTouched();
          isInvalid = isInvalid || o.invalid;
        });
        return isInvalid; // spot rates doesn't have summary and details view
        break;
    }
    this.inputForm.get('summaryRateView').markAsTouched();
    isInvalid = isInvalid || this.inputForm.get('summaryRateView').invalid;
    this.inputForm.get('detailsRateView').markAsTouched();
    isInvalid = isInvalid || this.inputForm.get('detailsRateView').invalid;

    return isInvalid;
  }

  compareRateTypes() {

  }

  onSectionSelectionChange(event: any) {

  }

  updateFxRateFormats(event: any, fxRateFormatKey: string) {
    const fxRateFormats: string[] = [];

    this.fxRateFormatOptions.forEach(fxRateFormatOption => {
      if (fxRateFormatOption.key === fxRateFormatKey) {
        fxRateFormatOption.isChecked = event.checked;
      }
      if (fxRateFormatOption.isChecked) {
        fxRateFormats.push(fxRateFormatOption.key);
      }
    });
    this.inputForm.controls['fxRateFormats'].setValue(fxRateFormats);
  }


}
